import { useParams, useNavigate } from 'react-router';
import { useProject, useProductionLogs } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ArrowLeft,
  Edit,
  Loader2,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ChartTooltip,
  CartesianGrid,
} from '@t90/ui';
import { formatDate, formatNumber, formatCurrency } from '@t90/common';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(id!);
  // TODO: Replace with correct assignmentId string for this project when available
  const { data: productionLogs } = useProductionLogs(undefined);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-lg font-medium">Project not found</p>
        <p className="text-sm text-muted-foreground">
          The project you're looking for doesn't exist or has been removed.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/projects')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>
      </div>
    );
  }

  const progress = project.quantity
    ? Math.round(((project.units_completed ?? 0) / project.quantity) * 100)
    : 0;

  // Prepare chart data
  const chartData = productionLogs?.map((log) => ({
    date: formatDate(log.log_date),
    units: log.units_completed ?? 0,
  })) ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">{project.project_number}</h1>
            <Badge variant={getStatusVariant(project.project_status)}>{project.project_status}</Badge>
          </div>
          <p className="text-muted-foreground">
            {project.order?.customer?.customer_name ?? 'Unknown Customer'} • {project.service_item?.service_item_name ?? 'Unknown Service'}
          </p>
        </div>
        <Button onClick={() => navigate(`/projects/${id}/edit`)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit Project
        </Button>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Progress</p>
              <p className="text-2xl font-bold">
                {formatNumber(project.units_completed ?? 0)} / {formatNumber(project.quantity ?? 0)} units
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Completion</p>
              <p className="text-2xl font-bold">{progress}%</p>
            </div>
          </div>
          <div className="h-4 w-full rounded-full bg-muted">
            <div
              className="h-4 rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="production">Production</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow label="Project #" value={project.project_number} />
                <InfoRow label="Service" value={project.service_item?.service_item_name ?? '-'} />
                <InfoRow label="Status" value={project.project_status} />
                <InfoRow label="Location" value={project.location?.location_name ?? '-'} />
                <InfoRow
                  label="Standard Rate"
                  value={project.standard_rate ? formatCurrency(project.standard_rate) : '-'}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow
                  label="Start Date"
                  value={project.start_date ? formatDate(project.start_date) : '-'}
                />
                <InfoRow
                  label="Due Date"
                  value={project.due_date ? formatDate(project.due_date) : '-'}
                />
                <InfoRow
                  label="Completed Date"
                  value={project.completed_date ? formatDate(project.completed_date) : '-'}
                />
                <InfoRow label="Created" value={formatDate(project.created_at)} />
              </CardContent>
            </Card>
          </div>

          {project.project_description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{project.project_description}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="production" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Production Trend</CardTitle>
              <CardDescription>Daily units completed over time</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <ChartTooltip />
                      <Area
                        type="monotone"
                        dataKey="units"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary) / 0.2)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-12">
                  No production data available yet.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Production Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {productionLogs?.length ? (
                <div className="space-y-3">
                  {productionLogs.slice(0, 10).map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <p className="text-sm font-medium">{formatDate(log.log_date)}</p>
                        <p className="text-xs text-muted-foreground">
                          Trainee {log.trainee_id?.substring(0, 8) ?? 'Unknown'}...
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatNumber(log.units_completed ?? 0)} units</p>
                        <p className="text-xs text-muted-foreground">
                          {log.hours_worked?.toFixed(1) ?? '-'} hrs
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No production logs recorded yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quality Checks</CardTitle>
              <CardDescription>QC results for this project</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Quality checks coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Not Started':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
}
